import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { ChevronLeft, Save, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { Select, Checkbox } from "antd";
import { cn } from "../../utils/tailwindUtils";
import useApiMethods from "../../lib/useApiMethods";
import { EMPLOYEE_WEEK_OFF, EMPLOYEE_DEPARTMENT } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";

// Week off list structure matching your old code
const weekoffList = [
  { week: 1, monday: 'working_day', tuesday: 'working_day', wednesday: 'working_day', thursday: 'working_day', friday: 'working_day', saturday: 'fullday', sunday: 'fullday' },
  { week: 2, monday: 'working_day', tuesday: 'working_day', wednesday: 'working_day', thursday: 'working_day', friday: 'working_day', saturday: 'fullday', sunday: 'fullday' },
  { week: 3, monday: 'working_day', tuesday: 'working_day', wednesday: 'working_day', thursday: 'working_day', friday: 'working_day', saturday: 'fullday', sunday: 'fullday' },
  { week: 4, monday: 'working_day', tuesday: 'working_day', wednesday: 'working_day', thursday: 'working_day', friday: 'working_day', saturday: 'fullday', sunday: 'fullday' },
  { week: 5, monday: 'working_day', tuesday: 'working_day', wednesday: 'working_day', thursday: 'working_day', friday: 'working_day', saturday: 'fullday', sunday: 'fullday' },
];

const WeekOffAddEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fromEdit, editDetails, isActive } = location.state || {};

    const { fetch, create, update } = useApiMethods();

    const [formData, setFormData] = useState({
        code: "",
        weekoff_name: "",
        department: [],
        is_active: true,
        weekoff_rules: "",
        weekoff_list: weekoffList.map(obj => ({ ...obj })),
        notes: "",
        is_draft: false
    });

    const [departmentsList, setDepartmentsList] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch departments for dropdown
    const fetchDepartments = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                setDepartmentsList(apiData.data);
            } else {
                setDepartmentsList([]);
                ToastMsg('error', 'Failed to load departments');
            }
        } catch (error) {
            ToastMsg('error', error.message);
            setDepartmentsList([]);
        }
    }, [fetch]);

    useEffect(() => {
        fetchDepartments();
    }, []);

    // Load edit details - matching your old code structure
    useEffect(() => {
        if (fromEdit && editDetails) {
            // Reorder keys to match your old code structure
            const reorderKeys = (array, keyToMove) => {
                return array.map(item => {
                    const { [keyToMove]: value, ...rest } = item;
                    return { ...rest, [keyToMove]: value };
                });
            };

            setFormData({
                id: editDetails.id || editDetails.weekoff_id,
                code: editDetails.code || "",
                weekoff_name: editDetails.weekoff_name || "",
                department: editDetails.department_id || [],
                is_active: editDetails.is_active ?? true,
                weekoff_rules: editDetails.weekoff_rules || "",
                weekoff_list: editDetails.weekoff_list?.length > 0 
                    ? reorderKeys(editDetails.weekoff_list, "sunday") 
                    : weekoffList.map(obj => ({ ...obj })),
                notes: editDetails.notes || "",
                is_draft: editDetails.is_draft || false
            });
        }
    }, [fromEdit, editDetails]);

    // Handle form field changes
    const handleChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Handle checkbox changes for week off rules - matching your old code logic
    const handleCheckboxChange = (weekIndex, day) => {
        setFormData(prev => {
            const updatedWeekoffList = [...prev.weekoff_list];
            const currentDayStatus = updatedWeekoffList[weekIndex][day];
            
            // Toggle between 'working_day' and 'fullday' exactly like your old code
            if (currentDayStatus === 'working_day') {
                updatedWeekoffList[weekIndex][day] = 'fullday';
            } else if (currentDayStatus === 'fullday') {
                updatedWeekoffList[weekIndex][day] = 'working_day';
            }
            
            return {
                ...prev,
                weekoff_list: updatedWeekoffList
            };
        });
    };

    // Generate week off rules exactly like your old code
    const generateWeekOffRule = (weekList) => {
        let output = [];
        let allDays = {};
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

        // Initialize allDays object
        days.forEach(day => {
            allDays[day] = true;
        });

        // Check if all days are full-day off throughout the weeks
        days.forEach(day => {
            weekList.forEach(week => {
                if (week[day] !== 'fullday') {
                    allDays[day] = false;
                }
            });
        });

        // Generate the output string based on the patterns detected
        days.forEach(day => {
            if (allDays[day]) {
                output.push(`All ${day.charAt(0).toUpperCase() + day.slice(1)}`);
            }
        });

        // Check for alternate days in the week for full days (leave)
        days.forEach(day => {
            const isAlternate = weekList.every((week, index) => 
                (index % 2 === 0 && week[day] === 'fullday') || 
                (index % 2 !== 0 && week[day] === 'working_day')
            );
            if (isAlternate) {
                output.push(`Alternate ${day.charAt(0).toUpperCase() + day.slice(1)}`);
            }
        });

        return output.join(', ');
    };

    const handleCancel = () => {
        navigate("/configuration/weekoff");
    };

    // Prepare payload for API
    const preparePayload = (isDraft = false) => {
        const weekoffRules = generateWeekOffRule(formData.weekoff_list);
        
        return {
            ...formData,
            department: formData.department,
            weekoff_rules: weekoffRules || "",
            is_draft: isDraft
        };
    };

    // Handle save (both create and update)
    const handleSave = async (isDraft = false) => {
        // Validation
        if (!formData.weekoff_name?.trim()) {
            ToastMsg("error", "Week off name is required");
            return;
        }
        if (formData.department.length === 0 && !isDraft) {
            ToastMsg("error", "Please select at least one department");
            return;
        }

        try {
            setLoading(true);
            const payload = preparePayload(isDraft);
            let response;

            if (fromEdit && formData.id) {
                response = await update(EMPLOYEE_WEEK_OFF, payload);
            } else {
                response = await create(EMPLOYEE_WEEK_OFF, payload);
            }

            if (response?.status === "success") {
                const message = isDraft ? "Week off saved as draft successfully" : 
                              fromEdit ? "Week off updated successfully" : "Week off created successfully";
                ToastMsg("success", message);
                navigate("/configuration/weekoff");
            } else {
                ToastMsg("error", response?.message || `Failed to ${fromEdit ? 'update' : 'save'} week off`);
            }
        } catch (error) {
            console.error("Error saving week off:", error);
            ToastMsg("error", error.message || `Failed to ${fromEdit ? 'update' : 'save'} week off`);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveClick = () => {
        handleSave(false);
    };

    const handleSaveAsDraft = () => {
        handleSave(true);
    };

    // Check if form has data for draft button
    const hasFormData = Object.entries(formData).some(([key, value]) => 
        !['is_active', 'id', 'is_draft', 'weekoff_list'].includes(key) && 
        value !== "" && value !== null && value !== undefined && 
        !(Array.isArray(value) && value.length === 0)
    );

    // Week Off Rules Component matching your old code structure
    const WeekOffRulesGrid = () => {
        const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

        return (
            <div className="md:col-span-2 space-y-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Week Off Rules <span className="text-red-500">*</span>
                </label>
                
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                    {/* Header row */}
                    <div className="grid grid-cols-8 gap-3 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">Week</div>
                        {dayLabels.map(day => (
                            <div key={day} className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Week rows - matching your old code structure */}
                    {formData.weekoff_list.map((week, weekIndex) => (
                        <div key={week.week} className="grid grid-cols-8 gap-3 mb-3 items-center">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                                Week {week.week}
                            </div>
                            {dayKeys.map(day => (
                                <div key={day} className="flex justify-center">
                                    <Checkbox
                                        checked={week[day] === 'working_day'}
                                        onChange={() => handleCheckboxChange(weekIndex, day)}
                                        className={cn(
                                            "custom-checkbox",
                                            week[day] === 'working_day' ? "custom-checkbox-green" : "custom-checkbox-red"
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}

                    {/* Legend */}
                    <div className="flex gap-6 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 text-xs">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-600 rounded"></div>
                            <span className="text-gray-600 dark:text-gray-400">Working Day</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-red-600 rounded"></div>
                            <span className="text-gray-600 dark:text-gray-400">Week Off</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeft className="h-5 w-5 cursor-pointer" onClick={handleCancel} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Week Off Policy" : "Add Week Off Policy"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Configure week off rules and assign to departments.
                    </p>
                </div>

                {/* Active Status Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <span
                        className={cn(
                            "text-sm font-medium",
                            formData.is_active ? "text-green-600" : "text-gray-500"
                        )}
                    >
                        {formData.is_active ? "Active" : "Inactive"}
                    </span>
                    <button
                        type="button"
                        onClick={() => handleChange("is_active", !formData.is_active)}
                        disabled={fromEdit && !isActive}
                        className={cn(
                            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                            formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                            fromEdit && !isActive && "opacity-50 cursor-not-allowed"
                        )}
                    >
                        <span
                            className={cn(
                                "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                formData.is_active ? "translate-x-4" : "translate-x-1"
                            )}
                        />
                    </button>
                </div>
            </div>

            {/* Form */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Week Off Code (only in edit mode) */}
                    {fromEdit && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Week Off Code
                            </label>
                            <Input
                                value={formData.code}
                                disabled
                                placeholder="Auto-generated code"
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white",
                                    "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                                )}
                            />
                        </div>
                    )}

                    {/* Week Off Name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Week Off Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={formData.weekoff_name}
                            onChange={(e) => handleChange("weekoff_name", e.target.value)}
                            placeholder="Enter week off name"
                            disabled={loading}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>

                    {/* Department */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Department <span className="text-red-500">*</span>
                        </label>
                        <Select
                            mode="multiple"
                            value={formData.department}
                            onChange={(value) => handleChange("department", value)}
                            options={departmentsList.map(dept => ({
                                value: dept.id,
                                label: dept.department_name
                            }))}
                            placeholder="Select departments"
                            disabled={loading}
                            className="w-full"
                            size="large"
                        />
                    </div>

                    {/* Week Off Rules */}
                    <WeekOffRulesGrid />

                    {/* Notes */}
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Notes
                        </label>
                        <Textarea
                            value={formData.notes}
                            onChange={(e) => handleChange("notes", e.target.value)}
                            placeholder="Enter any notes about this week off policy"
                            rows={3}
                            disabled={loading}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                            )}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                        className="flex items-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </Button>

                    {/* Draft Button - Only show in create mode */}
                    {!fromEdit && (
                        <Button
                            onClick={handleSaveAsDraft}
                            disabled={loading || !hasFormData}
                            className={cn(
                                "bg-gray-600 hover:bg-gray-700",
                                "text-white transition-colors duration-200",
                                "flex items-center gap-2",
                                (!hasFormData) && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            Save as Draft
                        </Button>
                    )}

                    <Button
                        onClick={handleSaveClick}
                        disabled={loading}
                        className={cn(
                            "bg-sidebar hover:bg-sidebar-primary",
                            "text-white transition-colors duration-200",
                            "flex items-center gap-2"
                        )}
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {loading ? "Saving..." : (fromEdit ? "Update" : "Save Policy")}
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
};

export default WeekOffAddEdit;